/**
 * Composant EmailDialog - Dialogue d'envoi d'email avec modèles
 */

import { PiecesJointesPicker } from '../../emails/PiecesJointesPicker';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../ui/select';
import { Send } from 'lucide-react';

export const EmailDialog = ({
  open,
  onOpenChange,
  selectedProspect,
  emailData,
  setEmailData,
  config,
  generateEmailPreview,
  onSendEmail,
  sending = false
}) => {
  const handleTemplateChange = (val) => {
    const preview = generateEmailPreview(val, selectedProspect);
    setEmailData({
      ...emailData, 
      template_type: val,
      preview_subject: preview.subject,
      preview_body: preview.body
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="email-dialog">
        <DialogHeader>
          <DialogTitle>Envoyer un email</DialogTitle>
          <DialogDescription>
            Destinataire: {selectedProspect?.prenom} {selectedProspect?.nom} ({selectedProspect?.email || 'aucune adresse e-mail'})
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4">
          <div>
            <Label htmlFor="template">Modèle d'email</Label>
            <Select 
              value={emailData.template_type} 
              onValueChange={handleTemplateChange}
            >
              <SelectTrigger data-testid="select-email-template">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {config.email_templates.map(t => (
                  <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {/* Prévisualisation et édition du contenu */}
          <div className="space-y-3 p-4 bg-slate-50 rounded-lg border">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-medium text-slate-700">Contenu de l'email (modifiable)</Label>
              {emailData.template_type !== 'custom' && (
                <span className="text-xs text-slate-500">Les balises {"{{prenom}}"}, {"{{nom}}"} sont remplacées automatiquement</span>
              )}
            </div>
            
            <div>
              <Label htmlFor="email_subject" className="text-xs text-slate-500">Objet</Label>
              <Input
                id="email_subject"
                value={emailData.template_type === 'custom' ? emailData.custom_subject : emailData.preview_subject}
                onChange={(e) => {
                  if (emailData.template_type === 'custom') {
                    setEmailData({...emailData, custom_subject: e.target.value});
                  } else {
                    setEmailData({...emailData, preview_subject: e.target.value});
                  }
                }}
                placeholder="Objet de l'email..."
                className="bg-white"
                data-testid="input-email-subject"
              />
            </div>
            
            <div>
              <Label htmlFor="email_body" className="text-xs text-slate-500">Message</Label>
              <Textarea
                id="email_body"
                value={emailData.template_type === 'custom' ? emailData.custom_body : emailData.preview_body}
                onChange={(e) => {
                  if (emailData.template_type === 'custom') {
                    setEmailData({...emailData, custom_body: e.target.value});
                  } else {
                    setEmailData({...emailData, preview_body: e.target.value});
                  }
                }}
                rows={10}
                placeholder="Votre message..."
                className="bg-white font-mono text-sm"
                data-testid="textarea-email-body"
              />
            </div>
          </div>
          <PiecesJointesPicker prospectId={selectedProspect?.prospect_id} value={emailData.pieces || []} onChange={(pieces) => setEmailData({ ...emailData, pieces })} />
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Annuler
          </Button>
          <Button 
            onClick={onSendEmail}
            disabled={sending || !selectedProspect?.email}
            className="bg-[#8DC63F] hover:bg-[#7AB62F]"
            data-testid="send-email-btn"
          >
            <Send className="h-4 w-4 mr-2" />
            {sending ? 'Envoi en cours…' : 'Envoyer'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EmailDialog;
